import { useEffect } from "react";

import history from "services/history";

interface IRdStationCredentials {
    clientToken: string;
    clientId: string;
    gaProperty?: string;
}

/**
 * Globals created by the RD Station tracking script loaded in public/indexes/index.<brand>.html.
 */
declare global {
    interface Window {
        RdstationPopup?: { init(clientToken: string, clientId: string, gaProperty?: string): void };
        RDCookieControl?: { analytics: { track: boolean } };
        credentials?: IRdStationCredentials;
    }
}

const POPUP_SELECTOR = 'body > div[id^="popup-"]';
const CLOSE_SELECTOR = '.rdstation-popup-js-close-button, .rdstation-popup-js-overlay, a[href="#closePopup"]';
const FLOATING_BAR_SELECTOR = 'div[data-embedded="true"]';
const REFRESH_DELAY_IN_MS = 300;

const withRdAnalyticsMuted = (callback: () => void) => {
    const cookieControl = window.RDCookieControl;
    const previousTrack = cookieControl?.analytics.track;

    if (cookieControl) {
        cookieControl.analytics.track = false;
    }

    try {
        callback();
    } finally {
        if (cookieControl && previousTrack !== undefined) {
            cookieControl.analytics.track = previousTrack;
        }
    }
};

const removePopup = (popup: HTMLElement) => {
    const isVisible = popup.style.display === "block";
    const closeButton = popup.querySelector<HTMLElement>(CLOSE_SELECTOR);

    // RD's close() also removes its scroll/exit-intent listeners, but it throws for floating bars that were never shown.
    if (closeButton && (isVisible || !popup.querySelector(FLOATING_BAR_SELECTOR))) {
        withRdAnalyticsMuted(() => closeButton.click());
    }

    if (popup.isConnected) {
        // RD skips showing a popup whose element is already "block", which keeps leftover listeners from reopening it.
        popup.style.display = "block";
        popup.remove();
    }
};

const refreshRdStationPopups = () => {
    const { RdstationPopup, credentials } = window;

    if (!RdstationPopup || !credentials) {
        return;
    }

    document.querySelectorAll<HTMLElement>(POPUP_SELECTOR).forEach(removePopup);

    RdstationPopup.init(credentials.clientToken, credentials.clientId, credentials.gaProperty);
};

/**
 * The RD Station script only fetches the popups configured for the URL on the first page load,
 * so on client-side navigation they have to be requested again for the new URL.
 */
const RdStationPopups = () => {
    useEffect(() => {
        let lastPathname = history.location.pathname;
        let refreshTimeout: number | undefined;

        const unlisten = history.listen(({ pathname }) => {
            if (pathname === lastPathname) {
                return;
            }

            lastPathname = pathname;

            window.clearTimeout(refreshTimeout);
            refreshTimeout = window.setTimeout(refreshRdStationPopups, REFRESH_DELAY_IN_MS);
        });

        return () => {
            unlisten();
            window.clearTimeout(refreshTimeout);
        };
    }, []);

    return null;
};

export default RdStationPopups;
